import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useKeepFocusedVisible } from '@/shared/hooks/use-keep-focused-visible';
import { useVisualViewport } from '@/shared/hooks/use-visual-viewport';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  titleClassName?: string;
  closeClassName?: string;
  closeIcon?: ReactNode;
  keepMounted?: boolean;
  onOpened?: () => void;
  /**
   * When false, the overlay stays `fixed inset-0` so the browser (and
   * `useKeepFocusedVisible` on the panel) handle the keyboard — safer around
   * cross-origin iframes that blur if the overlay is resized. Default pins the
   * overlay to visualViewport.
   */
  pinToVisualViewport?: boolean;
}

/** Modal bottom sheet: blurred backdrop + slide-up panel anchored to the bottom. */
function BottomSheet({ pinToVisualViewport = true, ...props }: BottomSheetProps) {
  if (!pinToVisualViewport) return <BottomSheetFrame {...props} />;
  return <PinnedBottomSheet {...props} />;
}

function PinnedBottomSheet(props: Omit<BottomSheetProps, 'pinToVisualViewport'>) {
  const { offsetTop, height, keyboardOpen } = useVisualViewport(
    Boolean(props.open || props.keepMounted),
  );
  return (
    <BottomSheetFrame
      {...props}
      overlayClassName="fixed left-0 right-0 z-50 flex flex-col justify-end bg-transparent font-body"
      overlayStyle={{ top: offsetTop, height }}
      panelClassName={cn(
        'relative mx-auto flex w-full max-w-app flex-col overflow-y-auto overflow-anchor-none overscroll-y-contain rounded-t-[24px] bg-mf-neutral-50 text-mf-neutral-900',
        keyboardOpen ? 'max-h-full' : 'max-h-[92%]',
      )}
    />
  );
}

function BottomSheetFrame({
  open,
  onClose,
  title,
  children,
  className,
  titleClassName,
  closeClassName,
  closeIcon,
  keepMounted = false,
  onOpened,
  overlayClassName = 'fixed inset-0 z-50 flex flex-col justify-end bg-transparent font-body',
  overlayStyle,
  panelClassName = 'relative mx-auto max-h-[92dvh] w-full max-w-app overflow-y-auto overflow-anchor-none overscroll-y-contain rounded-t-[24px] bg-mf-neutral-50 text-mf-neutral-900',
}: Omit<BottomSheetProps, 'pinToVisualViewport'> & {
  overlayClassName?: string;
  overlayStyle?: CSSProperties;
  panelClassName?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { spacerPx } = useKeepFocusedVisible(panelRef, title ? 56 : 16, open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const openedFired = useRef(false);
  const fireOpened = () => {
    if (openedFired.current || !open) return;
    openedFired.current = true;
    onOpened?.();
  };
  useEffect(() => {
    if (!onOpened) return;
    if (!open) {
      openedFired.current = false;
      return;
    }
    const t = setTimeout(fireOpened, 360);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onOpened]);

  if (!open && !keepMounted) return null;

  const hidden = !open;

  return (
    <div
      className={cn(
        hidden
          ? // Park off-screen: `invisible` + `fixed inset-0` is still sampled by
            // iOS 26 Safari and leaves a hole behind the Liquid Glass chrome.
            'pointer-events-none invisible fixed left-0 top-[200%] z-50 w-full'
          : overlayClassName,
      )}
      style={hidden ? undefined : overlayStyle}
      role="dialog"
      aria-modal="true"
      aria-hidden={hidden || undefined}
      inert={hidden}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-mf-neutral-900/40 backdrop-blur-[10px]',
          hidden ? 'opacity-0' : 'animate-in fade-in',
        )}
      />
      <div
        ref={panelRef}
        className={cn(
          panelClassName,
          hidden ? 'translate-y-full' : 'animate-in slide-in-from-bottom duration-300',
          className,
        )}
        onAnimationEnd={(e) => {
          if (onOpened && e.target === e.currentTarget) fireOpened();
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={cn(
            'absolute right-4 top-4 z-10 flex size-6 items-center justify-center text-mf-neutral-600 transition-colors hover:text-mf-neutral-900',
            closeClassName,
          )}
        >
          {closeIcon ?? <X className="size-6" />}
        </button>
        {title && (
          <div
            className={cn(
              'flex h-14 items-center justify-center border-b border-mf-neutral-900/10 bg-white px-12',
              titleClassName,
            )}
          >
            <h2 className="font-body text-lg font-bold leading-6 text-mf-neutral-900">{title}</h2>
          </div>
        )}
        {children}
        {spacerPx > 0 ? <div aria-hidden className="shrink-0" style={{ height: spacerPx }} /> : null}
      </div>
    </div>
  );
}

export { BottomSheet };
